import React, { useState } from "react";
import SafeMessageText from "./SafeMessageText";
import { safeHttpUrl } from "./hooks/useMessageProcessing";

// ── Data cards (rendered from typed structured props, not text markers) ────────

// Milestone timestamps arrive as "YYYY-MM-DD HH:mm:ss.s" — reformat to
// "01 Nov 2025, 11:28 PM" for display.
function formatMilestoneDate(dt) {
  const match = String(dt || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return dt;
  const [, y, mo, d, h, mi] = match;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  const datePart = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${datePart}, ${timePart}`;
}

function TrackResultCard({ data, hideSummary }) {
  const rows = [
    ["AWB", data.awb],
    ["Origin", data.origin],
    ["Destination", data.destination],
    // No branch row: customers see the destination city, never a branch name.
    ["EDD", data.edd],
  ].filter(([, v]) => v);
  const milestones = data.milestones || [];

  return (
    <div className="structured-card">
      <div className="structured-card-head">
        <div className="structured-card-title">Shipment Status</div>
        {data.current_status && (
          <span className="structured-card-status">{data.current_status}</span>
        )}
      </div>
      <div className="structured-card-body">
        {rows.map(([k, v]) => (
          <div key={k} className="structured-card-row">
            <span className="structured-card-label">{k}:</span>
            <span className="structured-card-value">{v}</span>
          </div>
        ))}
        {!hideSummary && data.summary && (
          <div className="structured-card-summary">{data.summary}</div>
        )}
        {milestones.length > 0 && (
          <>
            <div className="structured-card-section-title">Milestones</div>
            <div className="structured-card-timeline">
              {milestones.map((m, i) => {
                const [event, location, dt] = String(m).split("|").map((s) => s?.trim());
                return (
                  <div key={i} className="structured-card-milestone">
                    <span className="structured-card-milestone-dot" />
                    <div>
                      <div className="structured-card-milestone-event">{event}</div>
                      <div className="structured-card-milestone-meta">
                        {[location, formatMilestoneDate(dt)].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RateResultCard({ data, hideSummary }) {
  const services = data.services || [];

  return (
    <div className="structured-card">
      <div className="structured-card-head">
        <div className="structured-card-title">Shipping Rates</div>
      </div>
      <div className="structured-card-body">
        {(data.origin_pincode || data.destination_pincode) && (
          <div className="structured-card-route-row">
            <div>
              <div className="structured-card-route-label">FROM</div>
              <div className="structured-card-route-value">{data.origin_pincode}</div>
            </div>
            <div className="structured-card-route-arrow">→</div>
            <div style={{ textAlign: "right" }}>
              <div className="structured-card-route-label">TO</div>
              <div className="structured-card-route-value">{data.destination_pincode}</div>
            </div>
          </div>
        )}
        {!hideSummary && data.summary && (
          <div className="structured-card-summary">{data.summary}</div>
        )}
        {services.length > 0 && (
          <>
            <div className="structured-card-section-title">Available Services</div>
            <div className="structured-card-service-list">
              {services.map((s, i) => {
                const price = s.price ?? s.price_inr ?? "";
                return (
                  <div key={i} className={`structured-card-service-item${i === 0 ? " structured-card-service-item--best" : ""}`}>
                    <div>
                      <div className="structured-card-service-name">{s.service_name || s.name || "Service"}</div>
                      {s.eta && <div className="structured-card-service-eta">{s.eta}</div>}
                    </div>
                    <div className="structured-card-price">
                      {price !== "" ? `₹${price}` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Branch locator cards ──────────────────────────────────────────────────
// Rendered from `branch_result` (single nearest branch) / `branch_list`
// (multiple nearby branches, closest first) structured payloads.

function openMap(branch) {
  const query = branch.address || branch.name;
  const mapUrl = safeHttpUrl(branch.map_url);
  if (!mapUrl && !query) return;
  const url = mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function BranchResultCard({ data }) {
  return (
    <div className="structured-card">
      <div className="structured-card-head">
        <div className="structured-card-title">📍 {data.name || "Nearest Branch"}</div>
      </div>
      <div className="structured-card-body">
        {data.address && <div className="structured-card-branch-address">{data.address}</div>}
        <button className="structured-card-map-btn" onClick={() => openMap(data)}>
          View on map ↗
        </button>
      </div>
    </div>
  );
}

function BranchListCard({ data }) {
  const branches = data.branches || [];
  return (
    <div className="structured-card">
      <div className="structured-card-head">
        <div className="structured-card-title">Nearby Branches</div>
      </div>
      <div className="structured-card-branch-list">
        {branches.map((b, i) => (
          <div key={i} className="structured-card-branch-item">
            <div className="structured-card-branch-head">
              <span className="structured-card-branch-name">{b.name}</span>
              {b.distance && <span className="structured-card-branch-distance">{b.distance}</span>}
            </div>
            {b.address && <div className="structured-card-branch-address">{b.address}</div>}
            <button className="structured-card-map-btn" onClick={() => openMap(b)}>
              View on map ↗
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Interactive widgets ───────────────────────────────────────────────────────
// Real controlled components that reuse the existing chatbot.css classes
// (issue-selection-container / ticket-form-* / radio-options / checkbox-options /
// submit-option-btn). onSubmit(text) posts the same user turn the ticket sub-agent
// already parses; `submitted` disables + dims the widget after use.

function useSubmitOnce(onSubmit) {
  const [submitted, setSubmitted] = useState(false);
  const submit = (value) => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit(value);
  };
  return [submitted, submit];
}

function TicketFormCard({ data, onSubmit }) {
  const options = data.options || [];
  // Pre-fill from session memory / the brain's best-guess (prefill_issue only pre-selects
  // when it exactly matches an option). Falls back to empty so the field stays editable.
  const [issue, setIssue] = useState(
    options.includes(data.prefill_issue) ? data.prefill_issue : ""
  );
  const [cn, setCn] = useState(data.prefill_cn || "");
  const [mobile, setMobile] = useState(data.prefill_mobile || "");
  const [email, setEmail] = useState(data.prefill_email || "");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [submitted, submit] = useSubmitOnce(onSubmit);

  const handle = () => {
    if (!issue) return setError("Please select an issue.");
    if (!cn.trim()) return setError("Please enter your shipment/consignment number.");
    if (!/^\d{10}$/.test(mobile.trim())) return setError("Mobile number must be exactly 10 digits.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Please enter a valid email address.");
    if (description.trim().length < 10) return setError("Description must be at least 10 characters.");
    setError("");
    submit(
      `Issue: ${issue}; Shipment Number: ${cn.trim()}; Mobile: ${mobile.trim()}; ` +
        `Email: ${email.trim()}; Description: ${description.trim()}`
    );
  };

  return (
    <div className="issue-selection-container ticket-form ticket-form-card" style={submitted ? DISABLED : undefined}>
      <div className="ticket-form-eyebrow">Raise a ticket</div>
      {data.intro && <div className="issue-selection-text">{data.intro}</div>}
      <div className="ticket-form-field">
        <label className="ticket-form-label">Issue</label>
        <select className="ticket-form-input" value={issue} disabled={submitted}
          onChange={(e) => setIssue(e.target.value)}>
          <option value="" disabled>Select your issue</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="ticket-form-field">
        <label className="ticket-form-label">Shipment / Consignment number</label>
        <input className="ticket-form-input" value={cn} disabled={submitted}
          placeholder="e.g. 7D118007218" onChange={(e) => setCn(e.target.value)} />
      </div>
      <div className="ticket-form-field">
        <label className="ticket-form-label">Mobile number</label>
        <input className="ticket-form-input" value={mobile} disabled={submitted} type="tel"
          inputMode="numeric" maxLength={10} placeholder="10-digit mobile"
          onChange={(e) => setMobile(e.target.value)} />
      </div>
      <div className="ticket-form-field">
        <label className="ticket-form-label">Email</label>
        <input className="ticket-form-input" value={email} disabled={submitted} type="email"
          placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="ticket-form-field">
        <label className="ticket-form-label">Description</label>
        <textarea className="ticket-form-input ticket-form-textarea" value={description}
          disabled={submitted} rows={3} placeholder="Describe your issue (min 10 characters)"
          onChange={(e) => setDescription(e.target.value)} />
      </div>
      {error && !submitted && <div className="ticket-form-error">{error}</div>}
      <button className="submit-option-btn" disabled={submitted} onClick={handle}>
        {submitted ? "Submitted" : "Create ticket"}
      </button>
    </div>
  );
}

// Compact contact card (text-first ticket flow, option 1d): the ticket sub-agent
// gathers issue + shipment number + description as normal chat turns and only
// surfaces UI for the two fields that need format validation.
function ContactFormCard({ data, onSubmit }) {
  const [mobile, setMobile] = useState(data.prefill_mobile || "");
  const [email, setEmail] = useState(data.prefill_email || "");
  const [error, setError] = useState("");
  const [submitted, submit] = useSubmitOnce(onSubmit);

  const handle = () => {
    if (!/^\d{10}$/.test(mobile.trim())) return setError("Mobile number must be exactly 10 digits.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Please enter a valid email address.");
    setError("");
    submit(`Mobile: ${mobile.trim()}; Email: ${email.trim()}`);
  };

  return (
    <div className="issue-selection-container ticket-form ticket-form-card" style={submitted ? DISABLED : undefined}>
      <div className="ticket-form-contact-row">
        <input className="ticket-form-input" value={mobile} disabled={submitted} type="tel"
          inputMode="numeric" maxLength={10} placeholder="Mobile number"
          onChange={(e) => setMobile(e.target.value)} />
        <input className="ticket-form-input" value={email} disabled={submitted} type="email"
          placeholder="Email" onChange={(e) => setEmail(e.target.value)} />
      </div>
      {error && !submitted && <div className="ticket-form-error">{error}</div>}
      <button className="submit-option-btn" disabled={submitted} onClick={handle}>
        {submitted ? "Submitted" : "Create ticket"}
      </button>
    </div>
  );
}

// Authoritative ticket-creation confirmation (see shared/models.py::TicketResult on the
// backend). Read-only — the ticket ID is built straight from the real Freshdesk response,
// never from the assistant's own generated text, so it can't be wrong or fabricated.
function TicketResultCard({ data }) {
  return (
    <div className="issue-selection-container ticket-form ticket-form-card">
      <div className="ticket-form-eyebrow">✅ Ticket Created</div>
      <div className="ticket-form-field">
        <label className="ticket-form-label">Ticket ID</label>
        <div>{data.ticket_id}</div>
      </div>
    </div>
  );
}

const DISABLED = { opacity: 0.6, pointerEvents: "none" };

// type → component. Add a line here to support a new structured payload type.
const STRUCTURED_REGISTRY = {
  track_result: TrackResultCard,
  rate_result: RateResultCard,
  ticket_form: TicketFormCard,
  contact_form: ContactFormCard,
  branch_result: BranchResultCard,
  branch_list: BranchListCard,
  ticket_result: TicketResultCard,
};

function StructuredBlocks({ structured, onSubmit, hideSummary }) {
  if (!structured || structured.length === 0) return null;
  return structured.map((s, i) => {
    const Component = STRUCTURED_REGISTRY[s?.type];
    return Component ? (
      <Component key={i} data={s.data || {}} onSubmit={onSubmit} hideSummary={hideSummary} />
    ) : null;
  });
}

function StreamingCursor() {
  return <span className="cursor streaming-cursor" aria-hidden="true">▍</span>;
}

function MessageBubble({ message, isStreaming, grouped = false, onSubmit }) {
  const rowClass = `message-row message-enter${grouped ? " is-grouped" : ""}`;

  if (message.type === "SYSTEM") {
    return (
      <div className={rowClass}>
        <div className="bot-block">
          {message.body && (
            <SafeMessageText text={message.body} />
          )}
          {/* Card summary is a fallback: when the LLM wrote prose (body), the prose
              is the reply, so the redundant card summary is suppressed. */}
          <StructuredBlocks
            structured={message.structured}
            onSubmit={onSubmit}
            hideSummary={Boolean(message.body && message.body.trim())}
          />
          {isStreaming && <StreamingCursor />}
        </div>
      </div>
    );
  }

  return (
    <div className={`${rowClass} message-row--user`}>
      <div className="user-block">
        <div className="bot-msg">{message.body}</div>
      </div>
    </div>
  );
}

export default MessageBubble;
